import { useEffect } from "react";

const FONTS_ID = "ltb-v2-fonts";
const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Space+Grotesk:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap";

/** Injects the v2 web fonts once (kept after unmount to avoid re-flash). */
export function useV2Fonts() {
  useEffect(() => {
    if (document.getElementById(FONTS_ID)) return;
    const link = document.createElement("link");
    link.id = FONTS_ID;
    link.rel = "stylesheet";
    link.href = FONTS_HREF;
    document.head.appendChild(link);
  }, []);
}

/** Paints the body ink while v2 is mounted so overscroll never flashes white. */
export function useInkBody() {
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#0B0B0C";
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, []);
}

/**
 * Scroll reveal: every `.v2-reveal` (and `.v2-word` container marked with
 * `data-words`) gets `.is-in` once it enters the viewport. One observer for
 * the whole page; elements are unobserved after reveal.
 */
export function useReveal(rootRef: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );

    const observeAll = () => {
      root
        .querySelectorAll<HTMLElement>(".v2-reveal:not(.is-in), [data-words]:not(.is-in)")
        .forEach((el) => io.observe(el));
    };
    observeAll();

    // Sections that render after data fetches (reel, pricing, archives)
    // add new reveal targets later — pick them up as they appear.
    const mo = new MutationObserver(observeAll);
    mo.observe(root, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [rootRef]);
}

/**
 * Custom cursor ring (pointer: fine only). Lerps toward the mouse for a
 * soft lag and grows over interactive elements.
 */
export function useCursor(ringRef: React.RefObject<HTMLDivElement>) {
  useEffect(() => {
    const ring = ringRef.current;
    if (!ring) return;
    if (!window.matchMedia("(pointer: fine)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let tx = window.innerWidth / 2;
    let ty = window.innerHeight / 2;
    let x = tx;
    let y = ty;
    let raf = 0;

    const onMove = (e: MouseEvent) => {
      tx = e.clientX;
      ty = e.clientY;
      if (!ring.classList.contains("is-live")) {
        x = tx;
        y = ty;
        ring.classList.add("is-live");
      }
      const t = e.target as HTMLElement | null;
      const hot = !!t?.closest("a, button, [role='slider'], input, [data-cursor]");
      ring.classList.toggle("is-hover", hot);
    };
    const tick = () => {
      x += (tx - x) * 0.18;
      y += (ty - y) * 0.18;
      ring.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    raf = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, [ringRef]);
}
